import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SIDEBAR_MENU } from '../../constants/bancos';
import { useAuth } from '../../contexts/AuthContext';
import { logout } from '../../services/authService';
import { useTheme } from '../../contexts/Themecontext';
import { useUserProfile } from '../../contexts/UserContext';
import { ROLE_LABELS } from '../../services/userService';

const EXPANDED_W = 240;
const COLLAPSED_W = 68;

export default function Sidebar() {
  const { account } = useTheme();
  const { profile: authProfile } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { profile, canManageUsers, isSuperAdmin, subscription } = useUserProfile();
  const [expanded, setExpanded] = useState(() => window.innerWidth > 768);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const displayName = profile?.displayName || authProfile?.nome || 'Usuário';
  const iniciais = displayName
    .split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  const roleLabel = ROLE_LABELS[profile?.role] || 'Usuário';

  // Info do plano para exibir no rodapé
  const planoDisplay = isSuperAdmin
    ? 'Super Admin'
    : subscription?.planoNome || 'Sem plano';

  const sidebarWidth = expanded ? EXPANDED_W : COLLAPSED_W;

  return (
    <>
      {/* CSS variable override for layout */}
      <style>{`
        :root { --sidebar-w: ${sidebarWidth}px; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      <aside
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: sidebarWidth,
          background: 'var(--primary)',
          color: '#fff',
          padding: '16px 0',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100,
          fontFamily: 'var(--font)',
          transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
        }}
      >
        {/* Header — Logo + Hamburger */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'space-between' : 'center',
          padding: expanded ? '0 16px 0 20px' : '0 8px',
          marginBottom: 24,
          minHeight: 40,
        }}>
          {expanded && (
            <div
              onClick={() => navigate('/dashboard')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <div style={{
                width: 32, height: 32, minWidth: 32,
                background: 'rgba(255,255,255,0.08)',
                borderRadius: 7,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                color: 'var(--accent)',
                fontSize: '0.95rem',
              }}>
                {account?.logoUrl
                  ? <img src={account.logoUrl} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
                  : <span>{(account?.nomeEmpresa || 'S')[0]}</span>
                }
              </div>
              <span style={{ fontWeight: 700, fontSize: '1.1rem', transition: 'opacity 0.2s' }}>
                {account?.nomeEmpresa || 'SimulaBank'}
              </span>
            </div>
          )}

          {/* Hamburger */}
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: 'none',
              borderRadius: 6,
              width: 28,
              height: 28,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              cursor: 'pointer',
              padding: 0,
              flexShrink: 0,
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
            title={expanded ? 'Recolher menu' : 'Expandir menu'}
          >
            <span style={{
              display: 'block', width: 14, height: 2, borderRadius: 1,
              background: 'rgba(255,255,255,0.5)',
              transition: 'all 0.25s',
            }} />
            <span style={{
              display: 'block', width: expanded ? 14 : 10, height: 2, borderRadius: 1,
              background: 'rgba(255,255,255,0.5)',
              transition: 'all 0.25s',
            }} />
            <span style={{
              display: 'block', width: 14, height: 2, borderRadius: 1,
              background: 'rgba(255,255,255,0.5)',
              transition: 'all 0.25s',
            }} />
          </button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: expanded ? '0 10px' : '0 8px', overflowY: 'auto', overflowX: 'hidden' }}>
          {SIDEBAR_MENU.map((section) => (
            <div key={section.section} style={{ marginBottom: expanded ? 24 : 16 }}>
              {expanded && (
                <div style={{
                  fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.1em', color: 'rgba(255,255,255,0.25)',
                  padding: '0 10px', marginBottom: 6,
                  whiteSpace: 'nowrap', overflow: 'hidden',
                }}>
                  {section.section}
                </div>
              )}
              {!expanded && (
                <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '0 8px 8px' }} />
              )}
              {section.items
                .filter((item) => {
                  if (item.permission === 'manageUsers') return canManageUsers;
                  return true;
                })
                .map((item) => {
                  const isActive = pathname === item.path;
                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!item.soon) navigate(item.path);
                      }}
                      title={!expanded ? item.label : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: expanded ? 'flex-start' : 'center',
                        gap: expanded ? 10 : 0,
                        padding: expanded ? '9px 10px' : '9px 0',
                        borderRadius: 8,
                        marginBottom: 2,
                        color: isActive ? 'var(--accent)' : 'rgba(255,255,255,0.5)',
                        background: isActive ? 'rgba(0,214,143,0.1)' : 'transparent',
                        textDecoration: 'none',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        transition: 'all 0.15s',
                        opacity: item.soon ? 0.4 : 1,
                        cursor: item.soon ? 'default' : 'pointer',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        minHeight: 36,
                      }}
                    >
                      <span style={{
                        width: 20, minWidth: 20, textAlign: 'center',
                        fontSize: '0.95rem', flexShrink: 0,
                      }}>
                        {item.icon}
                      </span>
                      {expanded && (
                        <>
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                          {item.badge && (
                            <span style={{
                              background: 'rgba(0,214,143,0.15)', color: 'var(--accent)',
                              fontSize: '0.62rem', fontWeight: 700, padding: '2px 7px', borderRadius: 100,
                            }}>{item.badge}</span>
                          )}
                          {item.soon && (
                            <span style={{
                              background: 'rgba(245,166,35,0.15)', color: '#F5A623',
                              fontSize: '0.52rem', fontWeight: 700, padding: '2px 5px',
                              borderRadius: 100, textTransform: 'uppercase', letterSpacing: '0.04em',
                            }}>Breve</span>
                          )}
                        </>
                      )}
                    </a>
                  );
                })}
            </div>
          ))}

          {/* ── Super Admin Section ── */}
          {isSuperAdmin && (
            <div style={{ marginBottom: expanded ? 24 : 16 }}>
              {expanded && (
                <div style={{
                  fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.1em', color: 'rgba(239,68,68,0.5)',
                  padding: '0 10px', marginBottom: 6,
                  whiteSpace: 'nowrap', overflow: 'hidden',
                }}>
                  Administração
                </div>
              )}
              {!expanded && (
                <div style={{ height: 1, background: 'rgba(239,68,68,0.2)', margin: '0 8px 8px' }} />
              )}

              {[
                { icon: '🛡️', label: 'Painel Admin', path: '/admin' },
                { icon: '✏️', label: 'Editor Blog', path: '/admin-blog' },
              ].map((item) => {
                const isActive = pathname === item.path;
                return (
                  <a
                    key={item.path}
                    href={item.path}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(item.path);
                    }}
                    title={!expanded ? item.label : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: expanded ? 'flex-start' : 'center',
                      gap: expanded ? 10 : 0,
                      padding: expanded ? '9px 10px' : '9px 0',
                      borderRadius: 8,
                      marginBottom: 2,
                      color: isActive ? '#DC2626' : 'rgba(239,68,68,0.5)',
                      background: isActive ? 'rgba(239,68,68,0.1)' : 'transparent',
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      transition: 'all 0.15s',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      minHeight: 36,
                    }}
                  >
                    <span style={{
                      width: 20, minWidth: 20, textAlign: 'center',
                      fontSize: '0.95rem', flexShrink: 0,
                    }}>
                      {item.icon}
                    </span>
                    {expanded && (
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                    )}
                  </a>
                );
              })}
            </div>
          )}
        </nav>

        {/* User footer */}
        <div style={{
          padding: expanded ? '14px 16px' : '14px 8px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'flex-start' : 'center',
          gap: expanded ? 10 : 0,
          overflow: 'hidden',
        }}>
          <div
            onClick={() => !expanded && navigate('/conta')}
            title={!expanded ? displayName : undefined}
            style={{
              width: 34, height: 34, minWidth: 34, borderRadius: 8,
              background: isSuperAdmin ? 'rgba(239,68,68,0.15)' : 'rgba(0,214,143,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '0.75rem',
              color: isSuperAdmin ? '#DC2626' : 'var(--accent)',
              flexShrink: 0,
              cursor: expanded ? 'default' : 'pointer',
            }}
          >
            {iniciais}
          </div>
          {expanded && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '0.8rem', fontWeight: 600,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {displayName}
                </div>
                <div style={{
                  fontSize: '0.65rem',
                  color: isSuperAdmin ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.35)',
                }}>
                  {planoDisplay}
                </div>
              </div>
              <button
                onClick={handleLogout}
                style={{
                  color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem',
                  cursor: 'pointer', background: 'none', border: 'none', padding: 4,
                }}
                title="Sair"
              >
                ⏻
              </button>
            </>
          )}
        </div>
      </aside>
    </>
  );
}